import { BigNumber } from '@0x/utils'
import { formatPrecision } from 'app-lib/numberUtils'
import providerUtils from 'app-lib/providerUtils'
import { chainConfigs } from 'bzx-common/src/config/appConfig'
import { RootStore } from 'bzx-common/src/stores'
import * as mobx from 'mobx'
import { FormEvent } from 'react'

export interface IWrapNativeFormVMProps {
  autoSyncData: boolean
  direction: 'wrap' | 'unwrap'
  directionAllowed?: 'wrap' | 'unwrap'
  inputAmount: string
  nativeBalance: BigNumber
  nativeBalancePending: boolean
  swapPending: boolean
  useWrappedNative?: () => void
  wrappedBalance: BigNumber
  wrappedBalancePending: boolean
}

/**
 * Allows user to swap native to wrapped token for lending where the native token is not supported
 */
export default class WrapNativeFormVM implements IWrapNativeFormVMProps {
  autoSyncData = false
  direction: 'wrap' | 'unwrap' = 'wrap'
  directionAllowed?: 'wrap' | 'unwrap'
  inputAmount = ''
  nativeBalance = new BigNumber(0)
  nativeBalancePending = false
  swapPending = false
  rootStore: RootStore
  useWrappedNative?: () => void
  wrappedBalance = new BigNumber(0)
  wrappedBalancePending = false
  stopAutoSync?: mobx.Lambda

  get tokens() {
    const chainId = this.rootStore.web3Connection.chainId || 1
    return {
      native: { asset: chainConfigs[chainId].native.asset, balance: this.nativeBalance },
      wrapped: { asset: chainConfigs[chainId].native.wrapped, balance: this.wrappedBalance },
    }
  }

  get fromTo() {
    const { native, wrapped } = this.tokens
    return this.direction === 'wrap' ? { from: native, to: wrapped } : { from: wrapped, to: native }
  }

  set(prop: keyof IWrapNativeFormVMProps, value: any) {
    ;(this[prop] as any) = value
  }

  assign(props: Partial<IWrapNativeFormVMProps>) {
    Object.assign(this, props)
  }

  setToMax() {
    this.inputAmount = this.fromTo.from.balance.toFixed()
  }

  toggleReceive() {
    this.direction = this.direction === 'wrap' ? 'unwrap' : 'wrap'
    this.inputAmount = formatPrecision(this.fromTo.from.balance)
  }

  async getNativeBalance() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    this.assign({ nativeBalancePending: true })
    const { asset } = chainConfigs[chainId].native
    const nativeBalance = await providerUtils.getTokenBalanceOfUser(
      this.rootStore.fulcrumProvider,
      asset
    )
    this.assign({
      nativeBalance,
      nativeBalancePending: false,
    })
  }

  async getWrappedBalance() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    this.assign({ wrappedBalancePending: true })
    const { wrapped } = chainConfigs[chainId].native
    const wrappedBalance = await providerUtils.getTokenBalanceOfUser(
      this.rootStore.fulcrumProvider,
      wrapped
    )
    this.assign({ wrappedBalance, wrappedBalancePending: false })
  }

  async syncData() {
    await this.getNativeBalance()
    await this.getWrappedBalance()
    const balance = this.direction === 'wrap' ? this.nativeBalance : this.wrappedBalance
    this.inputAmount = formatPrecision(balance)
  }

  async wrapNativeToken(amount: BigNumber) {
    this.assign({ swapPending: true })
    const { transactionStore, fulcrumProvider } = this.rootStore
    const txSteps = providerUtils.wrapNativeToken(fulcrumProvider, amount)
    const tx = await transactionStore.run('swap', txSteps)
    if (tx.status === 'success') {
      this.syncData()
    } else {
      console.error(tx.error)
    }
    this.assign({ swapPending: false })
  }

  async unwrapNativeToken(amount: BigNumber) {
    this.assign({ swapPending: true })
    const { transactionStore, fulcrumProvider } = this.rootStore
    const txSteps = providerUtils.unwrapNativeToken(fulcrumProvider, amount)
    const tx = await transactionStore.run('swap', txSteps)
    if (tx.status === 'success') {
      this.syncData()
    } else {
      console.error(tx.error)
    }
    this.assign({ swapPending: false })
  }

  async submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (this.direction === 'wrap') {
      return this.wrapNativeToken(new BigNumber(this.inputAmount))
    } else {
      return this.unwrapNativeToken(new BigNumber(this.inputAmount))
    }
  }

  autoSync(enabled: boolean) {
    this.autoSyncData = enabled
  }

  clearData() {
    this.inputAmount = '0'
    this.nativeBalance = new BigNumber(0)
    this.wrappedBalance = new BigNumber(0)
  }

  init() {
    this.stopAutoSync = mobx.autorun(() => {
      const { chainId, walletAddress } = this.rootStore.web3Connection
      if (this.autoSyncData) {
        if (chainId && walletAddress) {
          this.clearData()
          this.syncData()
        } else {
          this.clearData()
        }
      }
    })
  }

  destroyVM() {
    this.stopAutoSync && this.stopAutoSync()
  }

  constructor(
    rootStore: RootStore,
    opts: { useWrapped?: () => void; directionAllowed?: 'wrap' | 'unwrap' } = {}
  ) {
    this.rootStore = rootStore
    if (opts.useWrapped) {
      this.useWrappedNative = opts.useWrapped
    }
    if (opts.directionAllowed) {
      this.directionAllowed = opts.directionAllowed
    }
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
  }
}
